import { Location } from '@angular/common';
import { Component, OnInit, OnDestroy, inject, signal, effect, NgZone, ChangeDetectorRef } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd, NavigationStart, NavigationCancel, NavigationError } from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { LanguageService } from './services/language.service';
import { SeoTitleI18nService } from './guards/seo-title.resolver';
import { AppVisibilityService } from './services/app-visibility.service';
import { animate, group, query, style, transition, trigger } from '@angular/animations';
import { StatusBarService, ThemeService, OfflinePopupComponent, NetworkService, ApiWebSocketService, CrashlyticsService } from '@shared';
import { ServiceZoneUiService } from './services/service-zone-ui.service';
import { CustomerCouponsCatalogService } from './services/customer-coupons-catalog.service';
import { UserNotificationSoundService } from './services/user-notification-sound.service';
import { AppVersionService } from './services/app-version.service';
import { UpdateRequiredModalComponent } from './components/update-required-modal.component';
import { ModalController } from '@ionic/angular/standalone';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, OfflinePopupComponent],
  template: `
    <div class="nav-overlay" [class.nav-overlay--active]="isNavigating()">
      <div class="nav-overlay__spinner" aria-hidden="true"></div>
    </div>

    <app-offline-popup></app-offline-popup>

    <div class="app-route-transition" [@appRouteAnimations]="prepareRoute(outlet)">
      <router-outlet #outlet="outlet"></router-outlet>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      height: 100%;
      min-height: 100%;
    }

    .app-route-transition {
      position: relative;
      height: 100%;
      min-height: 100%;
      overflow: hidden;
      background: #f9fafb; /* tailwind gray-50 */
    }

    /* Stack enter/leave on top of each other. Without this, leave+enter sit in
       normal document flow so the new page (e.g. vendor) appears below tabs and
       then jumps to the top when leave is removed. */
    .app-route-transition > :not(router-outlet) {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    @media (prefers-reduced-motion: reduce) {
      .app-route-transition {
        animation: none !important;
        transition: none !important;
      }
    }

    .nav-overlay {
      position: fixed;
      inset: 0;
      z-index: 9999;
      background: #f9fafb;
      opacity: 0;
      pointer-events: none;
      transition: opacity 120ms ease;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .nav-overlay--active {
      opacity: 1;
      pointer-events: auto; /* block double-taps during navigation */
    }

    .nav-overlay__spinner {
      width: 18px;
      height: 18px;
      border-radius: 50%;
      border: 2px solid rgba(48, 184, 74, 0.25);
      border-top-color: var(--cartco-green, #30B84A);
      animation: navSpin 700ms linear infinite;
    }

    @keyframes navSpin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `]
  ,
  animations: [
    // Outer (non-tab) transitions: opacity-only + absolute stack (same as tabs).
    // Never translateY — that reads as “slide up from bottom then snap to top”.
    trigger('appRouteAnimations', [
      transition('* <=> *', [
        query(':enter, :leave', [
          style({
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            willChange: 'opacity',
          }),
        ], { optional: true }),
        query(':enter', style({ opacity: 0, zIndex: 1 }), { optional: true }),
        group([
          query(':leave', [animate('140ms ease-out', style({ opacity: 0 }))], { optional: true }),
          query(':enter', [animate('200ms ease-out', style({ opacity: 1 }))], { optional: true }),
        ]),
      ]),
    ]),
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private location = inject(Location);
  private languageService = inject(LanguageService);
  private statusBar = inject(StatusBarService);
  private theme = inject(ThemeService);
  private appVisibility = inject(AppVisibilityService);
  private ngZone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);
  private networkService = inject(NetworkService);
  private apiWebSocketService = inject(ApiWebSocketService);
  private notificationSoundService = inject(UserNotificationSoundService);
  private appVersionService = inject(AppVersionService);
  private modalController = inject(ModalController);
  private crashlyticsService = inject(CrashlyticsService);
  private zoneUi = inject(ServiceZoneUiService);
  /** Keep zone-scoped coupon catalog warm and in sync with address switches. */
  private readonly customerCouponsCatalog = inject(CustomerCouponsCatalogService);
  private destroy$ = new Subject<void>();
  /** Ensures route SEO titles re-apply when the UI language changes. */
  private readonly seoTitleI18n = inject(SeoTitleI18nService);
  private appStateListenerHandle: PluginListenerHandle | null = null;
  private backButtonListener: PluginListenerHandle | null = null;
  private readonly visibilityChangeBound = this.onVisibilityChange.bind(this);
  private lastResumeHandledAt = 0;
  private readonly RESUME_DEBOUNCE_MS = 400;

  constructor() {
    void this.statusBar.configure({ isDark: this.theme.isDark() });
    effect(() => {
      const isDark = this.theme.isDark();
      void this.statusBar.updateForTheme(isDark);
    });
    effect(() => {
      const zoneId = this.zoneUi.primaryServiceZoneId();
      if (zoneId) {
        this.crashlyticsService.setAttribute('serviceZoneId', zoneId);
      }
    });
  }
  readonly isNavigating = signal(false);
  private navEndTimer: any = null;

  prepareRoute(outlet: RouterOutlet): string {
    // On first app load, the RouterOutlet may not be activated yet.
    // Accessing `activatedRoute` in that state throws NG04012.
    if (!outlet || !outlet.isActivated) {
      return this.router.url;
    }
    return outlet.activatedRoute?.snapshot?.url?.map(s => s.path).join('/') || this.router.url;
  }

  private splashDismissed = false;
  /** Minimum time the animated splash stays visible (feels intentional, not a flash). */
  private readonly SPLASH_MIN_MS = 1400;
  /** Hard cap so a hung route never traps the user on splash. */
  private readonly SPLASH_MAX_MS = 2800;

  /** Keep splash until first route settles AND min duration, with a hard max. */
  private scheduleSplashDismiss(): void {
    let uiReady = false;
    let minElapsed = false;

    const maybeDismiss = () => {
      if (uiReady && minElapsed) {
        this.dismissAnimatedSplash();
      }
    };

    setTimeout(() => {
      minElapsed = true;
      maybeDismiss();
    }, this.SPLASH_MIN_MS);

    setTimeout(() => this.dismissAnimatedSplash(), this.SPLASH_MAX_MS);

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        uiReady = true;
        maybeDismiss();
      });

    if (this.router.navigated) {
      uiReady = true;
      maybeDismiss();
    }
  }

  private dismissAnimatedSplash(): void {
    if (this.splashDismissed) {
      return;
    }
    this.splashDismissed = true;

    const splashScreen = document.getElementById('splash-screen');
    if (splashScreen) {
      splashScreen.classList.add('splash-screen--hide');
      setTimeout(() => splashScreen.remove(), 600);
    }
  }

  ngOnInit(): void {
    this.scheduleSplashDismiss();

    // Start notification sound service (plays sound when push received in foreground - works on Windows/phone)
    this.notificationSoundService.start();

    // Ensure button styles are applied on initial load
    setTimeout(() => {
      this.forceButtonTextColor();
    }, 1000);
    
    // Apply button styles on route changes
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        setTimeout(() => {
          this.forceButtonTextColor();
        }, 500);
      });

    // Global navigation overlay to avoid "white screen" gaps on slow API loads.
    // Skip for /tabs/* ↔ /tabs/* so the bottom nav never flashes away on tab switches.
    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          if (this.navEndTimer) {
            clearTimeout(this.navEndTimer);
            this.navEndTimer = null;
          }
          if (this.isIntraTabsNavigation(this.router.url, event.url)) {
            this.isNavigating.set(false);
            return;
          }
          // Leaving tabs (e.g. → vendor): pin scroll at top before paint so the
          // new page never briefly shows mid/bottom of the previous scroll.
          this.pinWindowScrollTop();
          this.isNavigating.set(true);
        }
        if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
          if (!(event instanceof NavigationCancel || event instanceof NavigationError)) {
            this.pinWindowScrollTop();
          }
          // Keep it visible for a minimum frame to prevent flicker.
          this.navEndTimer = setTimeout(() => {
            this.isNavigating.set(false);
            this.navEndTimer = null;
          }, 120);
        }
      });

    // Resume/visibility-triggered refresh: when app comes back from background or tab becomes visible.
    // Restores UI responsiveness (NgZone/change detection), rechecks network, reconnects WebSocket.
    document.addEventListener('visibilitychange', this.visibilityChangeBound);
    if (Capacitor.isNativePlatform()) {
      void App.addListener('appStateChange', (state: { isActive: boolean }) => {
        if (state.isActive) this.handleAppResume();
      }).then((handle) => {
        this.appStateListenerHandle = handle;
      });

      void App.addListener('backButton', () => {
        void this.ngZone.run(() => this.handleHardwareBack());
      }).then((handle) => {
        this.backButtonListener = handle;
      });
      // App version check: prompt to update when a newer version is on the store (production only).
      if (environment.production) {
        const platform = Capacitor.getPlatform();
        if (platform === 'android' || platform === 'ios') {
          setTimeout(() => void this.checkAppVersion(), 1500);
        }
      }
    }
  }

  private async checkAppVersion(): Promise<void> {
    try {
      const result = await this.appVersionService.shouldShowUpdateModal();
      const platform = Capacitor.getPlatform();
      const storeUrl =
        platform === 'ios'
          ? (result?.storeUrlIos ?? result?.storeUrl)
          : result?.storeUrl;
      if (!result || !storeUrl) return;
      const modal = await this.modalController.create({
        component: UpdateRequiredModalComponent,
        componentProps: {
          storeUrl,
          latestVersion: result.latestVersion ?? 'latest',
          forceUpdate: result.forceUpdate ?? false,
        },
        cssClass: 'update-required-modal',
      });
      await modal.present();
    } catch {
      // Ignore errors (e.g. network, modal creation)
    }
  }

  private onVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      this.handleAppResume();
    }
  }

  /**
   * Handles app resume from background. Runs inside NgZone to restore change detection.
   * Rechecks network (keeps OfflinePopupComponent in sync), reconnects WebSocket if needed.
   * Debounced to avoid duplicate work on rapid state changes.
   * Awaits network recheck before notifying pages so zone revalidation sees fresh connectivity.
   */
  private handleAppResume(): void {
    const now = Date.now();
    if (now - this.lastResumeHandledAt < this.RESUME_DEBOUNCE_MS) return;
    this.lastResumeHandledAt = now;

    this.ngZone.run(() => {
      this.cdr.detectChanges();
      this.apiWebSocketService.resumeFromBackground();
    });

    void this.notificationSoundService.ensureTokenRegistered().catch(() => {
      /* logged in service */
    });

    void (async () => {
      try {
        await this.networkService.recheck();
      } catch {
        // Ignore recheck failures; still notify so pages can attempt refresh.
      }
      this.ngZone.run(() => {
        this.appVisibility.notifyAppVisible();
      });
    })();
  }

  private async handleHardwareBack(): Promise<void> {
    const topModal = await this.modalController.getTop();
    if (topModal) {
      await topModal.dismiss();
      return;
    }

    const path = this.router.url.split('?')[0];
    if (path === '/tabs/home' || path === '/tabs') {
      await App.minimizeApp();
      return;
    }

    this.location.back();
  }

  /** True when staying inside the tabs shell (bottom nav should remain visible). */
  private isIntraTabsNavigation(fromUrl: string, toUrl: string): boolean {
    const from = fromUrl.split('?')[0].split('#')[0];
    const to = toUrl.split('?')[0].split('#')[0];
    return from.startsWith('/tabs') && to.startsWith('/tabs');
  }

  /** Keep document scroll at top during outer route changes (tabs → vendor, etc.). */
  private pinWindowScrollTop(): void {
    if (typeof window === 'undefined') {
      return;
    }
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.navEndTimer) {
      clearTimeout(this.navEndTimer);
      this.navEndTimer = null;
    }

    document.removeEventListener('visibilitychange', this.visibilityChangeBound);
    if (this.appStateListenerHandle) {
      this.appStateListenerHandle.remove();
      this.appStateListenerHandle = null;
    }
    if (this.backButtonListener) {
      void this.backButtonListener.remove();
      this.backButtonListener = null;
    }
  }

  private forceButtonTextColor(): void {
    // Ensure primary buttons have white text and green background
    const buttons = document.querySelectorAll('ion-button[color="primary"]:not([fill="outline"]):not([fill="clear"])');
    
    buttons.forEach((button) => {
      const element = button as HTMLElement;
      
      // Set CSS variables for both text and background
      element.style.setProperty('--color', '#ffffff', 'important');
      element.style.setProperty('color', '#ffffff', 'important');
      element.style.setProperty('--background', 'var(--cartco-green)', 'important');
      element.style.setProperty('background', 'var(--cartco-green)', 'important');
      
      // Apply styles to shadow DOM
      const shadowRoot = element.shadowRoot;
      if (shadowRoot) {
        const nativePart = shadowRoot.querySelector('[part="native"]') as HTMLElement;
        if (nativePart) {
          nativePart.style.setProperty('--color', '#ffffff', 'important');
          nativePart.style.setProperty('color', '#ffffff', 'important');
          nativePart.style.setProperty('--background', 'var(--cartco-green)', 'important');
          nativePart.style.setProperty('background', 'var(--cartco-green)', 'important');
          
          // Force all child elements to be white
          const allChildren = nativePart.querySelectorAll('*');
          allChildren.forEach((child: Element) => {
            (child as HTMLElement).style.setProperty('color', '#ffffff', 'important');
          });
        }
        
        // Target button-inner and span elements
        const buttonInner = shadowRoot.querySelector('.button-inner') as HTMLElement;
        if (buttonInner) {
          buttonInner.style.setProperty('color', '#ffffff', 'important');
        }
        
        const spans = shadowRoot.querySelectorAll('span');
        spans.forEach((span: Element) => {
          (span as HTMLElement).style.setProperty('color', '#ffffff', 'important');
        });
      }
      
      // Ensure background is not transparent
      const computedStyle = window.getComputedStyle(element);
      const bgColor = computedStyle.backgroundColor;
      if (bgColor === 'rgba(0, 0, 0, 0)' || bgColor === 'transparent') {
        element.style.setProperty('--background', 'var(--cartco-green)', 'important');
        element.style.setProperty('background', 'var(--cartco-green)', 'important');
      }
    });
  }
}
